
import { Component } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Layouts
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

// Network
import { request } from "app/network";
import { useMaterialUIController } from "context";

// Date Utils
import { formatLocalDateTime } from "utils/dateFormatter";

// DataTable (using simple table for logs as cards might be too bulky)
import DataTable from "components/ui/Tables/DataTable";

interface AdminActionLogState {
    logs: any[];
    loading: boolean;

    // Pagination
    pageSize: number;
    pageNumber: number;
    totalCount: number;

    // Filters
    searchKeyword: string;
    actionType: string | null;
    startDate: string;
    endDate: string;

    // Detail Dialog
    detailDialogOpen: boolean;
    selectedLog: any | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_admin_action_log_page_size";

class AdminActionLogScreenClass extends Component<any, AdminActionLogState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        // Default date range: Last 7 days
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 7);

        this.state = {
            logs: [],
            loading: false,
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            searchKeyword: "",
            actionType: null,
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0],
            detailDialogOpen: false,
            selectedLog: null,
        };
    }

    componentDidMount() {
        this.loadLogs();
    }

    loadLogs = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                SearchKeyword: this.state.searchKeyword || undefined,
                ActionType: this.state.actionType || undefined,
                StartDate: this.state.startDate ? this.state.startDate : undefined,
                EndDate: this.state.endDate ? `${this.state.endDate}T23:59:59` : undefined,
                // TenantSeqId can be added if we want to filter by tenant context
            };

            const res = await request("AdminActionLog.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const logs = (pagedList.items || [])
                    .map((log: any) => ({
                        seqId: log.seqId,
                        adminName: log.adminName || "-",
                        tenantName: log.tenantName || "전체(Global)",
                        actionType: log.actionType,
                        targetType: log.targetType,
                        targetId: log.targetId,
                        description: log.actionDescription,
                        ipAddress: log.ipAddress,
                        crtDt: log.crtDt ? formatLocalDateTime(log.crtDt) : "-",
                        beforeData: log.beforeData,
                        afterData: log.afterData,
                    }));

                this.setState({
                    logs,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false, logs: [], totalCount: 0 });
            }
        } catch (error) {
            console.error("Failed to load admin logs:", error);
            this.setState({ loading: false });
        }
    };

    handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchKeyword: event.target.value });
    };

    handleSearchSubmit = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            this.setState({ pageNumber: 1 }, this.loadLogs);
        }
    }

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, this.loadLogs);
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, this.loadLogs);
    };

    handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
        this.setState({ [field]: value } as any, () => {
            // Optional: Auto-reload or require search button
        });
    }

    handleDetailOpen = (log: any) => {
        this.setState({ selectedLog: log, detailDialogOpen: true });
    };

    handleDetailClose = () => {
        this.setState({ detailDialogOpen: false, selectedLog: null });
    };

    getActionColor = (actionType: string) => {
        switch (actionType?.toUpperCase()) {
            case 'CREATE': return 'success';
            case 'UPDATE': return 'info';
            case 'DELETE': return 'error';
            default: return 'dark';
        }
    };

    render() {
        const { logs, loading, pageSize, pageNumber, totalCount, searchKeyword, startDate, endDate, actionType } = this.state;
        const { darkMode } = this.props;

        const tableColumns = [
            { Header: "일시", accessor: "crtDt", width: "15%" },
            { Header: "관리자", accessor: "adminName", width: "10%" },
            {
                Header: "유형", accessor: "actionType", width: "8%", Cell: ({ value }: any) => (
                    <MDBox display="flex" alignItems="center">
                        <MDBox
                            width="0.5rem"
                            height="0.5rem"
                            borderRadius="50%"
                            bgColor={this.getActionColor(value)}
                            mr={1}
                        />
                        <MDTypography variant="caption" fontWeight="medium">
                            {value}
                        </MDTypography>
                    </MDBox>
                )
            },
            { Header: "대상", accessor: "targetType", width: "10%" },
            { Header: "대상 ID", accessor: "targetId", width: "10%" },
            { Header: "설명", accessor: "description", width: "20%" },
            { Header: "아이피", accessor: "ipAddress", width: "10%" },
            {
                Header: "상세", accessor: "detail", width: "7%", isSorted: false, Cell: ({ row }: any) => (
                    <MDButton variant="text" color="info" onClick={() => this.handleDetailOpen(row.original)}>
                        <Icon>visibility</Icon> 조회
                    </MDButton>
                )
            },
        ];

        // Transform for DataTable
        const tableRows = logs.map(log => ({
            ...log,
            detail: log // helper for cell
        }));


        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    <Card>
                        <MDBox p={3}>
                            {/* Header & Filters */}
                            {/* Header & Filters */}
                            <MDBox mb={3}>
                                <MDBox display="flex" alignItems="center" mb={2}>
                                    <Icon fontSize="medium">history_edu</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        관리자 액션 로그
                                    </MDTypography>
                                </MDBox>

                                <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                                    <MDInput
                                        type="date"
                                        label="시작일"
                                        value={startDate}
                                        onChange={(e: any) => this.handleDateChange('startDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                        sx={{
                                            "& input::-webkit-calendar-picker-indicator": {
                                                filter: this.props.darkMode ? "invert(1)" : "none"
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption">-</MDTypography>
                                    <MDInput
                                        type="date"
                                        label="종료일"
                                        value={endDate}
                                        onChange={(e: any) => this.handleDateChange('endDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                        sx={{
                                            "& input::-webkit-calendar-picker-indicator": {
                                                filter: this.props.darkMode ? "invert(1)" : "none"
                                            }
                                        }}
                                    />

                                    <Autocomplete
                                        value={actionType}
                                        onChange={(_, newValue) => this.setState({ actionType: newValue })}
                                        options={["CREATE", "UPDATE", "DELETE"]}
                                        renderInput={(params) => <MDInput {...params} label="유형" />}
                                        size="small"
                                        sx={{ width: 150 }}
                                    />

                                    <MDBox width="15rem">
                                        <MDInput
                                            placeholder="검색어 (관리자명, 대상ID)"
                                            value={searchKeyword}
                                            onChange={this.handleSearch}
                                            onKeyDown={this.handleSearchSubmit}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Icon fontSize="small" sx={{ color: this.props.darkMode ? "#ffffff !important" : "inherit" }}>search</Icon>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </MDBox>
                                    <MDButton variant="gradient" color="info" onClick={() => { this.setState({ pageNumber: 1 }, this.loadLogs) }}>
                                        조회
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                            <Divider />
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={2}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={pageSize.toString()}
                                        options={["10", "20", "50", "100"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) this.handlePageSizeChange(Number(newValue));
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary" ml={1}>
                                        개씩 보기 (Total: {totalCount})
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            {/* Data Table */}
                            {loading ? (
                                <MDBox display="flex" justifyContent="center" p={5}>
                                    <CircularProgress color="info" />
                                </MDBox>
                            ) : (
                                <DataTable
                                    table={{ columns: tableColumns, rows: tableRows }}
                                    showTotalEntries={false}
                                    isSorted={false}
                                    noEndBorder
                                    entriesPerPage={false} // Custom pagination below
                                    noPagination
                                />
                            )}

                            {/* Pagination */}
                            {totalCount > 0 && (
                                <MDBox mt={3} display="flex" justifyContent="center">
                                    <Pagination
                                        count={Math.ceil(totalCount / pageSize)}
                                        page={pageNumber}
                                        onChange={(_, page) => this.handlePageChange(page - 1)}
                                        color="primary"
                                        size="large"
                                        showFirstButton
                                        showLastButton
                                        sx={{
                                            '& .MuiPaginationItem-root': {
                                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                            },
                                            '& .Mui-selected': {
                                                backgroundColor: '#49a3f1 !important',
                                                color: '#ffffff !important',
                                            }
                                        }}
                                    />
                                </MDBox>
                            )}
                        </MDBox>
                    </Card>
                </MDBox>
                <Footer />

                {/* Detail Dialog */}
                <Dialog
                    open={this.state.detailDialogOpen}
                    onClose={this.handleDetailClose}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            backgroundColor: darkMode ? '#1a2035' : '#fff',
                            backgroundImage: 'none',
                        }
                    }}
                >
                    <DialogTitle sx={{ color: darkMode ? '#fff' : '#000' }}>
                        로그 상세 정보
                    </DialogTitle>
                    <DialogContent>
                        {this.state.selectedLog && (
                            <MDBox>
                                <MDTypography variant="h6" gutterBottom color={darkMode ? "white" : "dark"}>
                                    {this.state.selectedLog.actionType} - {this.state.selectedLog.targetType} ({this.state.selectedLog.targetId})
                                </MDTypography>
                                <Divider />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <MDTypography variant="button" fontWeight="bold" color="text">변경 전 데이터 (Before)</MDTypography>
                                        <MDBox
                                            component="pre"
                                            p={2}
                                            bgColor={darkMode ? "#2c3e50" : "#f5f5f5"}
                                            borderRadius="lg"
                                            sx={{ overflow: "auto", maxHeight: "300px", fontSize: "0.80rem", color: darkMode ? "#ecf0f1" : "#333" }}
                                        >
                                            {this.state.selectedLog.beforeData
                                                ? tryFormatJson(this.state.selectedLog.beforeData)
                                                : "데이터 없음 (None)"}
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <MDTypography variant="button" fontWeight="bold" color="text">변경 후 데이터 (After)</MDTypography>
                                        <MDBox
                                            component="pre"
                                            p={2}
                                            bgColor={darkMode ? "#2c3e50" : "#f5f5f5"}
                                            borderRadius="lg"
                                            sx={{ overflow: "auto", maxHeight: "300px", fontSize: "0.80rem", color: darkMode ? "#ecf0f1" : "#333" }}
                                        >
                                            {this.state.selectedLog.afterData
                                                ? tryFormatJson(this.state.selectedLog.afterData)
                                                : "데이터 없음 (None)"}
                                        </MDBox>
                                    </Grid>
                                </Grid>
                            </MDBox>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.handleDetailClose} color="info">
                            닫기
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        );
    }
}

// Helper to pretty print JSON
const tryFormatJson = (jsonStr: string) => {
    try {
        const obj = JSON.parse(jsonStr);
        return JSON.stringify(obj, null, 2);
    } catch (e) {
        return jsonStr;
    }
};

function AdminActionLogScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return <AdminActionLogScreenClass {...props} darkMode={darkMode} />;
}

export default AdminActionLogScreen;
